import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from "@angular/core";
import {
	ErrorClass,
	Fiat,
	Field,
	PasswordValidationError,
	PayoutAccount,
	Provider,
	Sepa,
	ValidationError
} from "@models";
import { PayAccountService } from "@main-services/pay_account.service";
import { UserService } from "@main-services/user.service";
import { FiatService } from "@main-services/currency/fiat.service";
import { Validateable } from "@models/interfaces";
import { Router } from "@angular/router";
import { BpModalHasSave } from "@models/interfaces/bp-modal-has-save";
import { truncateFixed } from "@helpers/utils";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { AgreementBundle, UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { take, tap } from "rxjs";
import {
	DYNAMIC_TERMS_TOKEN,
	TermsAndConditionsUpdatesComponent
} from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.component";
import { ConfirmModalService } from "@core-services/confirm-modal.service";

@Component({
	selector: "payout-account-detail",
	templateUrl: "./payout-account-detail-tile.component.html",
	standalone: false
})
export class PayoutAccountDetailTileComponent extends Validateable implements BpModalHasSave, OnInit {
	private readonly termsAndConditionsService = inject(TermsAndConditionsService);
	@ViewChild(DYNAMIC_TERMS_TOKEN) dynamic_terms: TermsAndConditionsUpdatesComponent;
	@Input()
	public account: PayoutAccount = null;
	@Input()
	public editable: string = "";
	@Input()
	public logoPosition: string = "top";
	@Input()
	public ignoreTerms: boolean = false;
	@Input()
	public sendEnabled: boolean = false;

	@Output()
	// eslint-disable-next-line @angular-eslint/no-output-native
	public cancelEvent = new EventEmitter<void>();
	@Output()
	// eslint-disable-next-line @angular-eslint/no-output-native
	public finish = new EventEmitter<PayoutAccount>();
	@Output()
	// eslint-disable-next-line @angular-eslint/no-output-native
	public onChangeNameClick = new EventEmitter<void>();
	@Output()
	// eslint-disable-next-line @angular-eslint/no-output-native
	public onPasswordProtectError = new EventEmitter<void>();

	protected readonly cdr = inject(ChangeDetectorRef);

	private storage = {};
	public providerTerms = false;
	protected accountValidated: boolean = false;
	protected sepaSupported: boolean = true;
	public showTermsSwitch = false;

	public bundles: AgreementBundle[] = [];
	public dynamictermAndConditionActive = false;

	public isDynamictermAndConditionActive$ = this.termsAndConditionsService.isDynamictermAndConditionActive$.pipe(
		tap((isActive) => {
			this.dynamictermAndConditionActive = isActive;
		})
	);

	public constructor(
		protected infoService: InfoService,
		protected fiatService: FiatService,
		protected translationService: TranslationService,
		protected masterdataService: MasterdataService,
		protected userService: UserService,
		protected profileFacade: NewProfileFacade,
		protected modalService: ModalService,
		protected confirmModalService: ConfirmModalService,
		protected payService: PayAccountService,
		protected router: Router,
		protected apicallService: ApicallService
	) {
		super();
		this.providerTerms = false;
	}

	ngOnInit(): void {
		this.showTermsSwitch = this._activeTerms && this.isSaveAble() && !this.account.isUsed();
		if (!this.sendEnabled) {
			this.termsAndConditionsService
				.getAgreementBundleData$(UserJourney.FiatWithdrawal)
				.pipe(take(1))
				.subscribe((bundles: AgreementBundle[]) => (this.bundles = bundles));
		}
	}

	public logoUrl(p: PayoutAccount): string {
		return this.masterdataService.logoUrl(p);
	}

	public isSaveAble(): boolean {
		return (
			(!this.account.isUsed() ||
				(this.account.isUsed() && this.getFields().some((field: Field) => !this.readonly(field))) ||
				this.account.getId() === "") &&
			this.getFields().some((field: Field) => field.editable)
		);
	}

	public providerLogo(): string {
		return this.masterdataService.logoUrl(this.provider);
	}

	public get provider(): Provider {
		return this.masterdataService.getPayoutProviderById(this.account.getProviderId());
	}

	public getFields(): Field[] {
		const providerFields = this.provider.getFields();
		if (!this.account.hasFields(providerFields)) {
			this.account.addFields(providerFields);
		}
		return this.account.getFields();
	}

	public clearModelError(field: Field): void {
		if (this.account.getValidationError(field.getName()).length > 0) {
			this.account.clearError();
		}
	}

	public readonly(f: Field): boolean {
		// Users can only withdraw to bank accounts that are on their own name
		if (
			f.getName() === "payout_receiver_name" &&
			(!this.sendEnabled || (!!this.account.getId() && this.account.isUsed())) &&
			!this.userService.user.isBusinessUser()
		) {
			return true;
		}

		// Readonly disabled if account was NOT already used to withdraw money or is "Payout account label (ie. name)"
		if (!this.account.isUsed() || f.getName() === "name") {
			return false;
		}

		// Readonly depend on each fields (if the field is mandatory and it's already filled)
		const field = this.provider.getFields().find((cur: Field) => cur.getName() === f.getName());
		if (!this.storage.hasOwnProperty(f.getName())) {
			this.storage[field.getName()] = field.isMandatory() && f.getValue().trim().length > 0;
		}

		return this.storage[f.getName()];
	}

	public feeString(): string {
		const fees: string[] = [];
		let feeStr = "";
		const fee = this.provider.getFeeByFiatId(this.fiat.getId());
		const fiat = this.fiat;

		if (fee.getFeePercent() > 0) {
			feeStr += "+ " + this.round(fee.getFeePercent()) + "%";
		}

		if (fee.getFeeMax() > 0) {
			fees.push(this.translate("fee_max") + " " + this.round(fee.getFeeMax()) + " " + fiat.getSymbol());
		}

		if (fee.getFeeFix() > 0) {
			fees.push(this.translate("fee_fix") + " " + this.round(fee.getFeeFix()) + " " + fiat.getSymbol());
		}

		return feeStr + (fees.length > 0 ? " (" + fees.join(", ") + ")" : "");
	}

	private round(fix: number): string {
		return truncateFixed(fix, this.fiat.getPrecision());
	}

	private get fiat(): Fiat {
		return this.fiatService.getItemById(this.account.getFiatId());
	}

	public close(): void {
		this.providerTerms = false;
		this.cancelEvent.emit();
		this.cdr.markForCheck();
	}

	public delete(): void {
		this.confirmModalService
			.showConfirmModal(
				"delete_payout_account_title",
				"do_you_really_want_to_delete_payout_account",
				"ok",
				"cancel"
			)
			.subscribe((a) => {
				if (a) {
					this.payService.deletePayoutAccount(this.account).subscribe(() => this.close());
				}
			});
	}

	private get _activeTerms(): boolean {
		return !this.providerTerms && this.provider.hasTerms() && !this.ignoreTerms;
	}

	public save(): void {
		if (this.dynamictermAndConditionActive) {
			if (this.dynamicTermsInvalid) {
				this.dynamic_terms.wiggleIt();
				return;
			}
			if (this.bundles.length === 0) {
				this.payout();
				return;
			}
			this.termsAndConditionsService.acceptTerms(
				[UserJourney.FiatWithdrawal],
				this.dynamic_terms?.termsAndConditionsForm?.value,
				() => {
					this.continue();
				}
			);
		} else {
			this.continue();
		}
	}

	public continue(): void {
		if (!this.dynamictermAndConditionActive && this._activeTerms && !this.account.isUsed()) {
			this.wiggle("providerTerms");
			this.infoService.setError(
				this.translationService.translate("info_text_pay_account_provider_agb_not_checked")
			);
			return;
		}
		this.payout();
	}

	private payout(): void {
		this.account.clearError();
		this.payService.upsertCall(this.account).subscribe(
			(ans) => {
				this.finish.emit(ans);
			},
			(error) => {
				this.providerTerms = false;

				if (error instanceof ErrorClass) {
					if (error instanceof PasswordValidationError) {
						this.onPasswordProtectError.emit();
					} else if (error instanceof ValidationError) {
						if (error.responseErrors.length === 1) {
							this.infoService.setError(error.responseErrors[0].title);
						}
						this.account.setValidationErrors(error);
					}
				} else {
					console.error(error);
				}
			}
		);
	}

	private translate(s: string): string {
		return this.translationService.translate(s);
	}

	public showChangeName(field: Field): boolean {
		let show: boolean = false;
		if (
			field &&
			field.getName() === "payout_receiver_name" &&
			!this.userService.user.isVerified() &&
			this.readonly(field)
		) {
			show = true;
		}
		return show;
	}

	public handleChangeName(): void {
		this.onChangeNameClick.emit();
	}

	public getTerms(): string {
		return this.getProviderField("terms");
	}

	public getNameForProvider(): string {
		return this.getProviderField("name");
	}

	private getProviderField(field: string): string {
		return this.provider.getMultiLangField(field, this.userService.user.getLanguage());
	}

	public onValueChange(event: FocusEvent, field: Field): void {
		if (field.getName() === "iban" && !!field.getValue() && this.accountValidated) {
			this.accountValidated = false;
		}
	}

	public onInputFocusOut(event: FocusEvent, field: Field): void {
		// Do the call only for IBAN input focus out
		if (field.getName() === "iban" && field.getValue() && !this.accountValidated) {
			const option = new ApicallOption(Sepa, { iban: field.getValue() });
			this.apicallService.post("iban/validate", option).subscribe(
				([sepa]: [Sepa]) => {
					this.sepaSupported = sepa.isSepaEnabled();
					if (!this.sepaSupported) {
						return;
					}
					this.setBankFieldData(true, sepa);
					this.accountValidated = true;
				},
				(error) => {
					if (error instanceof ErrorClass) {
						this.infoService.setError(error);
						if (error instanceof ValidationError) {
							this.setBankFieldData();
							this.accountValidated = false;
						}
					} else {
						console.error(error);
					}
				}
			);
		}
	}

	protected setBankFieldData(isValid: boolean = false, field: Sepa = new Sepa()): void {
		this.getFields().forEach((f) => {
			switch (f.getName()) {
				case "bank_name":
					f.setValue(isValid ? field.getBank() : "");
					break;
				case "bic":
					f.setValue(isValid ? field.getBic() : "");
					break;
				default:
					break;
			}
		});
	}

	public get dynamicTermsInvalid(): boolean {
		return this.bundles?.length > 0 && !this.dynamic_terms?.termsAndConditionsForm?.valid;
	}

	public get isButtonDisabled(): boolean {
		const staticTermsInvalid = !this.dynamictermAndConditionActive && !this.providerTerms;
		const termsInvalid = staticTermsInvalid || this.dynamicTermsInvalid;
		return termsInvalid && this.account.getId() === "" && !this.ignoreTerms;
	}
}
